import { Component, effect, inject } from '@angular/core';
import { PatientDashboardTabComponent } from '../../dashboard-tab/dashboard-tab.component';
import { ButtonDirective } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { DialogService } from 'primeng/dynamicdialog';
import { OrderSet } from 'src/app/calvin-emr-shared/models/order-set.model';
import { toTitleCase } from 'src/app/shared/utils/string.util';
import { OrderSetEditDialogComponent } from './order-set-edit-dialog/order-set-edit-dialog.component';
import { ORDER_SETS_FIELD, PROVIDER_ORDERS_FIELD } from 'src/app/global';
import { OrderSetViewDialogComponent } from './order-set-view-dialog/order-set-view-dialog.component';
import { Patient } from 'src/app/calvin-emr-shared/models/patient.model';
import { ProviderOrder } from 'src/app/calvin-emr-shared/models/provider-order.model';

@Component({
  selector: 'app-order-sets-tab',
  templateUrl: './order-sets-tab.component.html',
  styleUrl: './order-sets-tab.component.scss',
  standalone: true,
  providers: [DialogService],
  imports: [
    ButtonDirective,
    TableModule,
    TooltipModule,
  ],
})
export class OrderSetsTabComponent extends PatientDashboardTabComponent {

  dialogService = inject(DialogService);

  orderSets: OrderSet[] = [];
  patient: Patient | null = null;

  constructor() {
    super();
    effect(() => {
      const patient = this.patientStore.patient();
      this.orderSets = patient.orderSets;
      this.patient = patient;
    });
  }

  openOrderSetDialog(type: 'create' | 'edit', orderSet?: OrderSet) {
    const header = `${toTitleCase(type)} Order Set`;
    const data = orderSet ? { type: type, orderSet: orderSet } : { type: type };

    const dialogRef = this.dialogService.open(OrderSetEditDialogComponent, {
      header: header,
      width: '90%',
      data: data,
    });

    dialogRef?.onClose.subscribe((orderSet: OrderSet) => {
      if (orderSet) {
        if (type === 'create') {
          this.orderSets.push(orderSet);
        } else if (type === 'edit') {
          const indexToRemove = this.orderSets.findIndex((n) => n.id == orderSet.id);
          this.orderSets.splice(indexToRemove, 1, orderSet);
        }

        this.updatePatientField(ORDER_SETS_FIELD, this.orderSets);
      }
    });
  }

  /**
   * return true if the provider orders in this orderSet have been
   * applied to the MAR. I.e., their isScheduled flag is true. If the
   * first provider order's isScheduled is true, then all are true, and
   * vice versa.
   * @param orderSet
   */
  hasBeenApplied(orderSet: OrderSet): boolean {
    const po = this.patient?.providerOrders.filter((po: ProviderOrder) => po.id === orderSet.providerOrderIds[0]);
    if (!po || po.length === 0) {
      return false;
    }
    return po[0].isScheduled;
  }

  viewOrderSet(orderSet: OrderSet) {
    this.dialogService.open(OrderSetViewDialogComponent, {
      header: 'View Order Set',
      data: orderSet,
      width: '50%',
    });
  }

  deleteOrderSet(orderSet: OrderSet) {
    this.orderSets = this.orderSets.filter((o) => {
      return o.id !== orderSet.id;
    });
    this.updatePatientField(ORDER_SETS_FIELD, this.orderSets);
  }

  /**
   * Change the isScheduled field in the provider orders in this order set to true.
   * This will make them show up on the MAR.
   * @param orderSet
   */
  applyOrderSet(orderSet: OrderSet) {
    this.patient!.providerOrders.forEach((po: ProviderOrder) => {
      if (orderSet.providerOrderIds.includes(po.id)) {
        po.isScheduled = true;
      }
    });
    this.updatePatientField(PROVIDER_ORDERS_FIELD, this.patient!.providerOrders);
  }

}
